<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

// [VITE] [TODO] We are using vue-virtual-scroll for now, since that seemed the simplest way to migrate
// from the current one. But we should consider using tanstack virtual in the future
// https://tanstack.com/virtual/latest/docs/framework/vue/examples/variable
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import ChatListHeader from './ChatListHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabsWithFilter from './widgets/ChatTypeTabsWithFilter.vue';
import ConversationItem from './ConversationItem.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import IntersectionObserver from './IntersectionObserver.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useEventListener } from '@vueuse/core';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  getUserRole,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import {
  filterByAssigneeTabSubStatus,
  getStatusFilterFromAssigneeTabSubFilter,
  toggleFilterSelection,
} from '../store/modules/conversations/helpers';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import { getBaseConversationPaginationMeta } from 'dashboard/helper/chatListPaginationHelper';
import {
  getConversationMenuAssigneeType,
  getConversationMenuDefaultStatus,
  getConversationMenuFilterConversationType,
  getConversationMenuFilterOptions,
  getConversationMenuFilterStatus,
  getConversationMenuFilterTitle,
  getConversationMenuUnreadFilter,
  getWorkflowConversationAssigneeType,
  getWorkflowConversationStatus,
  isConversationMenuView,
  isWorkflowConversationView,
  normalizeConversationMenuFilterForStatus,
  shouldShowConversationMenuDropdown as canShowConversationMenuDropdown,
  shouldShowConversationStatusFilter as canShowConversationStatusFilter,
} from 'dashboard/helper/conversationViewHelper';

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  conversationView: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings, updateUISettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const conversationListRef = ref(null);
const conversationDynamicScroller = ref(null);

provide('contextMenuElementTarget', conversationDynamicScroller);

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ME);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const myTabSubFilter = ref('');
const allTabSubFilter = ref('');
const conversationMenuFilter = ref('');
const selectedInboxFilter = ref('');
const previousStatusBeforeSubFilter = ref(null);
const showAdvancedFilters = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const isMarkingAllAsRead = ref(false);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

// computed
const intersectionObserverOptions = computed(() => {
  return {
    root: conversationListRef.value,
    rootMargin: '100px 0px 100px 0px',
  };
});

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const currentUserRole = computed(() => {
  return getUserRole(currentUser.value, currentAccountId.value);
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const isConversationMenuMode = computed(() => {
  return isConversationMenuView(props.conversationView);
});

const isWorkflowView = computed(() => {
  return isWorkflowConversationView(props.conversationView);
});

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count:
      {
        all: conversationStats.value.allUnreadMessagesCount,
        me: conversationStats.value.mineCount,
      }[key] ??
      conversationStats.value[countKey] ??
      0,
  }));
});

const currentConversationMenuFilter = computed(() => {
  if (!isConversationMenuMode.value || isWorkflowView.value) {
    return '';
  }

  return conversationMenuFilter.value || props.conversationView;
});

const currentAssigneeType = computed(() => {
  if (isWorkflowView.value) {
    return getWorkflowConversationAssigneeType({
      conversationView: props.conversationView,
      currentUserRole: currentUserRole.value,
      userPermissions: userPermissions.value,
    });
  }

  if (isConversationMenuMode.value) {
    return getConversationMenuAssigneeType({
      conversationView: props.conversationView,
      currentUserRole: currentUserRole.value,
      userPermissions: userPermissions.value,
    });
  }

  return activeAssigneeTab.value;
});

const currentConversationType = computed(() => {
  if (isWorkflowView.value) {
    return props.conversationType || '';
  }

  if (isConversationMenuMode.value) {
    return getConversationMenuFilterConversationType(
      currentConversationMenuFilter.value
    );
  }

  return props.conversationType || '';
});

const currentConversationMenuUnreadFilter = computed(() => {
  if (!isConversationMenuMode.value || isWorkflowView.value) {
    return null;
  }

  return getConversationMenuUnreadFilter(currentConversationMenuFilter.value);
});

const conversationMenuFilterOptions = computed(() => {
  if (!isConversationMenuMode.value || isWorkflowView.value) {
    return [];
  }

  return getConversationMenuFilterOptions({
    conversationView: props.conversationView,
    t,
  });
});

const showConversationMenuDropdown = computed(() => {
  return (
    !hasAppliedFiltersOrActiveFolders.value &&
    !isWorkflowView.value &&
    canShowConversationMenuDropdown(props.conversationView)
  );
});

const showConversationStatusFilter = computed(() => {
  return (
    !hasAppliedFiltersOrActiveFolders.value &&
    !isWorkflowView.value &&
    canShowConversationStatusFilter(props.conversationView)
  );
});

const hasConversationInboxContext = computed(() => {
  return Number(props.conversationInbox) > 0;
});

const showInboxFilter = computed(() => {
  return (
    props.conversationView === 'all' &&
    !hasAppliedFiltersOrActiveFolders.value &&
    !hasConversationInboxContext.value &&
    !props.teamId &&
    !props.label &&
    !props.conversationType
  );
});

const inboxFilterOptions = computed(() => {
  return [
    {
      label: t('CHAT_LIST.CHANNEL_FILTER.ALL'),
      value: '',
    },
    ...inboxesList.value
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(inboxItem => ({
        label: inboxItem.name,
        value: String(inboxItem.id),
      })),
  ];
});

const showLegacyAssigneeTabs = computed(() => {
  return (
    !hasAppliedFiltersOrActiveFolders.value &&
    !isConversationMenuMode.value &&
    !isWorkflowView.value
  );
});

const showMarkAllRead = computed(() => {
  return !hasAppliedFiltersOrActiveFolders.value && !isWorkflowView.value;
});

const isMineConversationView = computed(() => props.conversationView === 'mine');

const markAllButtonLabel = computed(() => {
  return isMineConversationView.value
    ? t('CHAT_LIST.MARK_ALL_RESOLVED.BUTTON')
    : t('CHAT_LIST.MARK_ALL_READ.BUTTON');
});

const markAllButtonTooltip = computed(() => markAllButtonLabel.value);

const showAssigneeInConversationCard = computed(() => {
  if (isWorkflowView.value) {
    return (
      hasAppliedFiltersOrActiveFolders.value || props.conversationView === 'all'
    );
  }

  if (isConversationMenuMode.value) {
    return (
      hasAppliedFiltersOrActiveFolders.value ||
      props.conversationView !== 'mine'
    );
  }

  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentAssigneeTabSubFilter = computed(() => {
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return myTabSubFilter.value;
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL) {
    return allTabSubFilter.value;
  }
  return '';
});

const isMySubFilterActive = computed(() => {
  return (
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME &&
    !!currentAssigneeTabSubFilter.value
  );
});

const assigneeTabSubFilterOptions = computed(() => {
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return [
      {
        label: t('CHAT_LIST.ASSIGNEE_TAB_SUB_FILTER.ME.NORMAL'),
        value: 'normal',
      },
      {
        label: t('CHAT_LIST.ASSIGNEE_TAB_SUB_FILTER.ME.ARCHIVED'),
        value: 'archived',
      },
    ];
  }

  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL) {
    return [
      {
        label: t('CHAT_LIST.ASSIGNEE_TAB_SUB_FILTER.ALL.UNREAD'),
        value: 'unread',
      },
      {
        label: t('CHAT_LIST.ASSIGNEE_TAB_SUB_FILTER.ALL.READ'),
        value: 'read',
      },
    ];
  }

  return [];
});

const currentPageFilterKey = computed(() => {
  if (hasAppliedFiltersOrActiveFolders.value) {
    return 'appliedFilters';
  }

  if (props.conversationView === 'mine_archived') {
    return 'mine_archived';
  }

  return currentAssigneeType.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const countsByType = {
    me: conversationStats.value.mineCount || 0,
    unassigned: conversationStats.value.unAssignedCount || 0,
    all: conversationStats.value.allCount || 0,
    mine_unassigned:
      (conversationStats.value.mineCount || 0) +
      (conversationStats.value.unAssignedCount || 0),
  };

  return countsByType[currentAssigneeType.value] || 0;
});

const baseConversationPagination = computed(() =>
  getBaseConversationPaginationMeta({
    currentPage: currentPage.value,
    totalCount: activeAssigneeTabCount.value,
    hasAppliedFiltersOrActiveFolders: hasAppliedFiltersOrActiveFolders.value,
  })
);

const selectedInboxId = computed(() => {
  if (hasConversationInboxContext.value) {
    return Number(props.conversationInbox);
  }
  if (selectedInboxFilter.value) return Number(selectedInboxFilter.value);

  return undefined;
});

const conversationFilters = computed(() => {
  const status = isWorkflowView.value
    ? getWorkflowConversationStatus(props.conversationView)
    : activeStatus.value;

  return {
    inboxId: selectedInboxId.value,
    assigneeType: currentAssigneeType.value,
    status,
    sortBy: activeSortBy.value,
    page: baseConversationPagination.value.nextPage,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: currentConversationType.value || undefined,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (props.conversationView === 'all') {
    return t('SIDEBAR.ALL_CONVERSATIONS');
  }
  if (props.conversationView === 'mine') {
    return t('SIDEBAR.MY_ASSIGNED_CONVERSATIONS');
  }
  if (props.conversationView === 'mine_archived') {
    return t('SIDEBAR.ARCHIVE');
  }
  if (isConversationMenuMode.value) {
    return getConversationMenuFilterTitle({
      conversationView: props.conversationView,
      currentFilter: currentConversationMenuFilter.value,
      t,
    });
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (isWorkflowView.value || isConversationMenuMode.value) {
      if (currentAssigneeType.value === 'me') {
        localConversationList = [...mineChatsList.value(filters)];
      } else {
        localConversationList = [...allChatList.value(filters)];
      }
    } else if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    localConversationList = localConversationList.filter(conversation => {
      if (isWorkflowView.value) {
        return true;
      }

      if (isConversationMenuMode.value) {
        if (currentConversationMenuUnreadFilter.value === 'unread') {
          return Number(conversation.unread_count || 0) > 0;
        }

        if (currentConversationMenuUnreadFilter.value === 'read') {
          return Number(conversation.unread_count || 0) === 0;
        }

        return true;
      }

      return filterByAssigneeTabSubStatus(conversation, {
        assigneeType: activeAssigneeTab.value,
        assigneeTabSubFilter: currentAssigneeTabSubFilter.value,
      });
    });
  }

  return localConversationList;
});

const effectiveEndReached = computed(() => {
  return (
    hasCurrentPageEndReached.value ||
    baseConversationPagination.value.hasLoadedAllConversations
  );
});

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    effectiveEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { order_by: orderBy } = filterBy;
  if (isWorkflowView.value) {
    activeStatus.value = getWorkflowConversationStatus(props.conversationView);
    conversationMenuFilter.value = '';
  } else if (isConversationMenuMode.value) {
    activeStatus.value = getConversationMenuDefaultStatus(
      props.conversationView
    );
    conversationMenuFilter.value = props.conversationView;
  } else {
    const { status } = filterBy;
    activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  }
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
  // [VITE] removing this since the library has changed
  // nextTick(() => {
  //   // Addressing a known issue in the virtual list library where dynamically added items
  //   // might not render correctly. This workaround involves a slight manual adjustment
  //   // to the scroll position, triggering the list to refresh its rendering.
  //   const virtualList = conversationListRef.value;
  //   const scrollToOffset = virtualList?.scrollToOffset;
  //   const currentOffset = virtualList?.getOffset() || 0;
  //   if (scrollToOffset) {
  //     scrollToOffset(currentOffset + 1);
  //   }
  // });
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store
    .dispatch('fetchAllConversations', {
      pageFilterKey: currentPageFilterKey.value,
      skipStatsUpdate:
        !isConversationMenuMode.value && isMySubFilterActive.value,
    })
    .then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function updateStatusFilter(status) {
  if (!status || activeStatus.value === status) return;

  activeStatus.value = status;
  store.dispatch('setChatStatusFilter', status);
  updateUISettings({
    conversations_filter_by: {
      ...(uiSettings.value?.conversations_filter_by || {}),
      status,
      order_by: activeSortBy.value,
    },
  });
  resetAndFetchData();
}

function onConversationMenuFilterChange(value) {
  if (!isConversationMenuMode.value) return;

  conversationMenuFilter.value = value;
  const nextStatus = getConversationMenuFilterStatus(value);

  if (activeStatus.value !== nextStatus) {
    updateStatusFilter(nextStatus);
    return;
  }

  resetAndFetchData();
}

function onInboxFilterChange(value) {
  if (!showInboxFilter.value || selectedInboxFilter.value === value) return;

  selectedInboxFilter.value = value;
  resetAndFetchData();
}

function loadMoreConversations() {
  if (effectiveEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Add a method to handle scroll events
function handleScroll() {
  const scroller = conversationDynamicScroller.value;
  if (scroller && scroller.hasScrollbar) {
    const { scrollTop, scrollHeight, clientHeight } = scroller.$el;
    if (scrollHeight - (scrollTop + clientHeight) < 100) {
      loadMoreConversations();
    }
  }
}

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    previousStatusBeforeSubFilter.value = null;

    if (selectedTab === wootConstants.ASSIGNEE_TYPE.ME) {
      myTabSubFilter.value = '';
    }
    if (selectedTab === wootConstants.ASSIGNEE_TYPE.ALL) {
      allTabSubFilter.value = '';
    }

    if (activeStatus.value !== wootConstants.STATUS_TYPE.OPEN) {
      updateStatusFilter(wootConstants.STATUS_TYPE.OPEN);
      return;
    }
    if (!currentPage.value) {
      fetchConversations();
    }
  }
}

function onAssigneeTabSubFilterChange(value) {
  let nextSubFilter = '';
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    nextSubFilter = toggleFilterSelection(myTabSubFilter.value, value);
    myTabSubFilter.value = nextSubFilter;
  } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL) {
    nextSubFilter = toggleFilterSelection(allTabSubFilter.value, value);
    allTabSubFilter.value = nextSubFilter;
  }

  const nextStatus = getStatusFilterFromAssigneeTabSubFilter({
    assigneeType: activeAssigneeTab.value,
    assigneeTabSubFilter: nextSubFilter,
  });

  if (nextSubFilter) {
    if (nextStatus) {
      if (previousStatusBeforeSubFilter.value === null) {
        previousStatusBeforeSubFilter.value = activeStatus.value;
      }
      updateStatusFilter(nextStatus);
    }
    return;
  }

  if (previousStatusBeforeSubFilter.value !== null) {
    updateStatusFilter(previousStatusBeforeSubFilter.value);
    previousStatusBeforeSubFilter.value = null;
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
    if (isConversationMenuMode.value) {
      conversationMenuFilter.value = normalizeConversationMenuFilterForStatus({
        conversationView: props.conversationView,
        status: value,
      });
    }
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationView: props.conversationView,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function markAllRead() {
  if (isMarkingAllAsRead.value) return;

  isMarkingAllAsRead.value = true;

  try {
    if (isMineConversationView.value) {
      await store.dispatch('resolveAllMineConversations', {
        params: {
          inbox_id: conversationFilters.value.inboxId,
          assignee_type: 'me',
          status: conversationFilters.value.status,
          labels: conversationFilters.value.labels,
          team_id: conversationFilters.value.teamId,
          conversation_type: conversationFilters.value.conversationType,
        },
      });

      await resetAndFetchData();
      store.dispatch('conversationStats/get', conversationFilters.value);
      useAlert(t('CHAT_LIST.MARK_ALL_RESOLVED.SUCCESS'));
      return;
    }

    const conversationIds = conversationList.value
      .filter(conversation => Number(conversation.unread_count || 0) > 0)
      .map(conversation => conversation.id);

    await store.dispatch('markAllMessagesRead', {
      params: {
        inbox_id: conversationFilters.value.inboxId,
        assignee_type: currentAssigneeType.value,
        status: conversationFilters.value.status,
        labels: conversationFilters.value.labels,
        team_id: conversationFilters.value.teamId,
        conversation_type: conversationFilters.value.conversationType,
      },
      conversationIds,
    });

    store.dispatch('conversationStats/markAllRead', {
      assigneeType: currentAssigneeType.value,
    });
    store.dispatch('conversationStats/get', conversationFilters.value);

    if (
      currentAssigneeTabSubFilter.value === 'unread' ||
      currentConversationMenuUnreadFilter.value === 'unread'
    ) {
      await resetAndFetchData();
    }

    useAlert(t('CHAT_LIST.MARK_ALL_READ.SUCCESS'));
  } catch (error) {
    useAlert(
      t(
        isMineConversationView.value
          ? 'CHAT_LIST.MARK_ALL_RESOLVED.ERROR'
          : 'CHAT_LIST.MARK_ALL_READ.ERROR'
      )
    );
  } finally {
    isMarkingAllAsRead.value = false;
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(conversationId, status, snoozedUntil) {
  store
    .dispatch('toggleStatus', {
      conversationId,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
    });
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return store.getters.getConversationById(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (
    hasAppliedFiltersOrActiveFolders.value ||
    (!isConversationMenuMode.value && isMySubFilterActive.value)
  ) {
    return;
  }
  store.dispatch('conversationStats/get', conversationFilters.value);
});

useEventListener(conversationDynamicScroller, 'scroll', handleScroll);

onMounted(() => {
  setFiltersFromUISettings();
  store.dispatch('setChatListFilters', conversationFilters.value);
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('updateConversationStatus', toggleConversationStatus);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationView),
  () => {
    setFiltersFromUISettings();
    store.dispatch('setChatStatusFilter', activeStatus.value);
    resetAndFetchData();
  }
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 bg-n-solid-1 conversations-list-wrap"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[340px] 2xl:w-[412px]',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      :show-conversation-menu-dropdown="showConversationMenuDropdown"
      :conversation-menu-filter-value="currentConversationMenuFilter"
      :conversation-menu-filter-options="conversationMenuFilterOptions"
      :show-basic-filter="showConversationStatusFilter"
      :show-inbox-filter="showInboxFilter"
      :inbox-filter-value="selectedInboxFilter"
      :inbox-filter-options="inboxFilterOptions"
      :show-mark-all-read="showMarkAllRead"
      :is-marking-all-as-read="isMarkingAllAsRead"
      :mark-all-button-label="markAllButtonLabel"
      :mark-all-button-tooltip="markAllButtonTooltip"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @conversation-menu-filter-change="onConversationMenuFilterChange"
      @inbox-filter-change="onInboxFilterChange"
      @mark-all-read="markAllRead"
    />

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <ChatTypeTabsWithFilter
      v-if="showLegacyAssigneeTabs"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      :sub-filter-value="currentAssigneeTabSubFilter"
      :sub-filter-options="assigneeTabSubFilterOptions"
      :is-on-expanded-layout="isOnExpandedLayout"
      @chat-tab-change="updateAssigneeTab"
      @sub-filter-change="onAssigneeTabSubFilterChange"
    />

    <p
      v-if="!chatListLoading && !conversationList.length"
      class="flex items-center justify-center p-4 overflow-auto"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <ConversationBulkActions
      v-if="selectedConversations.length"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="toggleSelectAll"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />
    <div
      ref="conversationListRef"
      class="flex-1 overflow-hidden conversations-list hover:overflow-y-auto"
      :class="{ 'overflow-hidden': isContextMenuOpen }"
    >
      <DynamicScroller
        ref="conversationDynamicScroller"
        :items="conversationList"
        :min-item-size="24"
        class="w-full h-full overflow-auto"
      >
        <template #default="{ item, index, active }">
          <!--
            If we encounter resizing issues, we can set the `watchData` prop to true
            this will deeply watch the entire object instead of just size dependencies
            But it can impact performance
          -->
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
            :size-dependencies="[
              item.messages,
              item.labels,
              item.uuid,
              item.inbox_id,
            ]"
          >
            <ConversationItem
              :source="item"
              :label="label"
              :team-id="teamId"
              :folders-id="foldersId"
              :conversation-type="conversationType"
              :conversation-view="conversationView"
              :show-assignee="showAssigneeInConversationCard"
              @select-conversation="selectConversation"
              @de-select-conversation="deSelectConversation"
            />
          </DynamicScrollerItem>
        </template>
        <template #after>
          <div v-if="chatListLoading" class="flex justify-center my-4">
            <Spinner class="text-n-brand" />
          </div>
          <p
            v-else-if="showEndOfListMessage"
            class="p-4 text-center text-n-slate-11"
          >
            {{ $t('CHAT_LIST.EOF') }}
          </p>
          <IntersectionObserver
            v-else-if="!effectiveEndReached"
            :options="intersectionObserverOptions"
            @observed="loadMoreConversations"
          />
        </template>
      </DynamicScroller>
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
  </div>
</template>
